import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Repeat, Ticket, Calendar, MapPin, Mic2, ShoppingCart, Tag,
} from "lucide-react";

import { toast } from "sonner";

export default function ResalePage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyLoading, setBuyLoading] = useState(null);

  const fetchListings = useCallback(async () => {
    try {
      const res = await api.get("/resale", { params: { limit: 50 } });
      setListings(res.data.listings || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const handleBuy = async (listingId) => {
    if (!window.confirm("Confirmer l'achat de ce billet ? Le montant sera debite de vos credits.")) return;
    setBuyLoading(listingId);
    try {
      const res = await api.post("/resale/purchase", { listing_id: listingId });
      toast.success(res.data.message);
      fetchListings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'achat");
    }
    setBuyLoading(null);
  };

  const handleCancel = async (listingId) => {
    try {
      await api.delete(`/resale/listing/${listingId}`);
      fetchListings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="py-6 space-y-6" data-testid="resale-page">
      <div>
        <h1 className="font-heading font-extrabold text-2xl text-foreground flex items-center gap-2">
          <Repeat className="w-7 h-7 text-[#FF6B35]" />
          Marche secondaire
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Billets en revente securisee entre fans</p>
        <p className="text-[10px] text-muted-foreground mt-0.5">Prix plafonne a 110% du prix original (anti-speculation) - Commission 10%</p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map(i => <div key={i} className="bg-card rounded-3xl h-28 animate-pulse" />)}
        </div>
      ) : listings.length === 0 ? (
        <div className="text-center py-16">
          <Repeat className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground font-medium">Aucun billet en revente</p>
          <p className="text-sm text-muted-foreground mt-1">Les fans peuvent mettre leurs billets en vente depuis "Mes Billets"</p>
        </div>
      ) : (
        <div className="space-y-3">
          {listings.map((l) => {
            const isMine = l.seller_id === user?.id;
            return (
              <div key={l.id} className="bg-card border border-border rounded-3xl p-4 shadow-sm" data-testid={`resale-${l.id}`}>
                <div className="flex items-start gap-4">
                  <div className="bg-muted rounded-2xl p-3 text-center shrink-0 border border-border min-w-[52px]">
                    <p className="text-[10px] text-muted-foreground uppercase">
                      {new Date(l.event_date).toLocaleDateString("fr-FR", { month: "short" })}
                    </p>
                    <p className="font-heading font-extrabold text-xl text-foreground -mt-0.5">
                      {new Date(l.event_date).getDate()}
                    </p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-bold text-foreground truncate">{l.event_title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{l.category_name}</p>
                    <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-1 flex-wrap">
                      {l.event_time && (
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {l.event_time}</span>
                      )}
                      {l.event_location && (
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {l.event_location}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <Tag className="w-3 h-3" /> Vendeur: {l.seller_pseudo}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      {l.original_price > 0 && l.resale_price !== l.original_price && (
                        <span className="text-xs text-muted-foreground line-through">
                          {(l.original_price / 100).toFixed(2)} EUR
                        </span>
                      )}
                      <span className="font-heading font-extrabold text-lg text-[#FF6B35]">
                        {l.resale_price === 0 ? "Gratuit" : `${(l.resale_price / 100).toFixed(2)} EUR`}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        ({Math.round(l.resale_price / 100)} credits)
                      </span>
                    </div>
                  </div>
                  {isMine ? (
                    <button
                      onClick={() => handleCancel(l.id)}
                      className="text-xs text-red-400 hover:underline shrink-0"
                    >
                      Retirer
                    </button>
                  ) : (
                    <button
                      onClick={() => handleBuy(l.id)}
                      disabled={buyLoading === l.id}
                      className="bg-[#FF6B35] text-white font-bold px-4 py-2.5 rounded-full text-sm hover:scale-105 active:scale-95 transition-transform flex items-center gap-1.5 shrink-0 disabled:opacity-50"
                      data-testid={`buy-resale-${l.id}`}
                    >
                      <ShoppingCart className="w-4 h-4" />
                      {buyLoading === l.id ? "..." : "Acheter"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
